/**
 * Shared drafting-point constraint (docs/plans/2d/2d-full-parity-plan.md
 * Faz 0.4 + Faz 2.3): every CAD drafting tool resolves a raw ground point
 * through ONE pipeline — object snap (F3, resolved against the RAW cursor
 * point and winning over everything else), grid snap stepping (F9, store
 * cell size), polar tracking angle step (F10), then ortho (F8, relative to
 * the tool's base point).
 *
 * The pipeline also PUBLISHES the drafting feedback — the active osnap
 * marker and the statusbar `draftCursor` readout — from the same query the
 * click commits through, so the marker the user sees, the coordinate the
 * statusbar prints and the point the geometry lands on are always one and
 * the same (2D behavior).
 */

import * as THREE from 'three';
import { snapToGroundGrid, applyGroundOrtho } from '../../core/scene';
import { useSnapStore, type SnapPoint } from './SnapManager';
import { resolveOsnap } from './snapSystemSync';
import { useAppStore } from '../../state/store';
import { useViewStore } from '../../state/commandAuxStores';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';

/** Snap `to` onto the nearest polar-tracking ray through `base` when the
 *  angle deviation from a POLARANG multiple is within the tracking tolerance. */
function applyPolarConstraint(base: THREE.Vector3, to: THREE.Vector3): THREE.Vector3 {
    const stepDeg = useSystemVarsStore.getState().POLARANG || 90;
    const step = (stepDeg * Math.PI) / 180;
    const dx = to.x - base.x, dy = to.y - base.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return to;
    const ang = Math.atan2(dy, dx);
    const nearest = Math.round(ang / step) * step;
    // Track when within ~3 degrees of the guide ray.
    if (Math.abs(ang - nearest) > (3 * Math.PI) / 180) return to;
    return new THREE.Vector3(base.x + Math.cos(nearest) * len, base.y + Math.sin(nearest) * len, to.z);
}

/** Publish the drafting feedback for a resolved point: the osnap marker
 *  (null when no target was acquired) and the statusbar readout point.
 *  Both store setters drop content-equal writes, so pointermove storms
 *  cost at most one re-render when the feedback actually changes. */
function publishDraftFeedback(snap: SnapPoint | null, point: THREE.Vector3): void {
    const store = useSnapStore.getState();
    store.setActiveSnap(snap);
    store.setDraftCursor(point);
}

/**
 * Resolve a raw world point into the final drafting point.
 * @param raw      ground-plane intersection of the cursor ray
 * @param camera   active viewport camera (osnap aperture + ortho unaffected)
 * @param gl       renderer for pixel->world aperture conversion
 * @param base     ortho/polar reference point (last committed point), if any
 */
export function constrainDraftPoint(
    raw: THREE.Vector3,
    camera: THREE.Camera,
    gl: THREE.WebGLRenderer,
    base?: THREE.Vector3 | null
): THREE.Vector3 {
    // Running object snaps (F3) resolve against the RAW cursor point — the
    // aperture is measured around the cursor, not around a pre-stepped
    // point — and the acquired target wins over every other constraint
    // (2D behavior: grid/polar/ortho stand down for a real snap target).
    // `base` unlocks the direction-dependent perpendicular mode.
    const snap = resolveOsnap(raw, camera, gl, base);
    if (snap) {
        publishDraftFeedback(snap, snap.position);
        return snap.position;
    }

    const snapState = useSnapStore.getState();
    let p = raw.clone();
    // Grid snap stepping (F9): independent from object snap.
    if (snapState.gridSnapEnabled) {
        p = snapToGroundGrid(p, snapState.gridSize);
    }
    // Polar tracking (F10): angle-step constraint.
    if (base && useViewStore.getState().polarOn) {
        p = applyPolarConstraint(base, p);
    }
    // Ortho (F8 / Shift hold) relative to the last committed point. The
    // store's shiftOrthoActive flag implements the temporary Shift override.
    if (base && (useAppStore.getState().orthoMode || useAppStore.getState().shiftOrthoActive)) {
        p = applyGroundOrtho(base, p, true);
    }
    // No target under the cursor: marker rests, readout follows the
    // constrained cursor.
    publishDraftFeedback(null, p);
    return p;
}
